// src/lib/csv-codes-absents.js — CODES DÉJÀ IMPORTÉS, ABSENTS DU NOUVEAU
// RELEVÉ DU MÊME MOIS (relecture Maison Zen, défaut 6).
//
// Un séjour importé d'un premier relevé, puis annulé, n'apparaît plus dans le
// relevé suivant : sa ligne reste comptée et rien ne le disait. On le SIGNALE
// à l'écran, en texte seul. RIEN N'EST SUPPRIMÉ : Dimitri regarde et décide
// (un relevé de versements peut aussi simplement ne pas couvrir ce séjour).
//
// Fonction PURE, aucune écriture.
//
// « Même mois » = les mois où ce relevé crée ou met à jour au moins une ligne
// (mois de rangement du plan). « Déjà importé » = une ligne du blob qui porte
// un code de la plateforme (code HM Airbnb / référence Booking) et vient d'un
// import CSV (marqueur d'origine, ou note « Import CSV » des imports d'avant
// le marqueur). Une ligne supprimée (marque de suppression) n'est pas signalée.
// Un code présent dans le relevé, même écarté (annulé, net ≤ 0), n'est pas
// « absent ».

import { formatMois } from "./format";
import { dateBasculeCsv } from "./csv-bascule";
import { transformV2Resa } from "./resas-v2-source";
import { computeInvoiceMonthByNights } from "./resa-invoice-month";

const CHAMP_CODE = { airbnb: "airbnbCode", booking: "bookingRef" };
const SEAU_SUPPRIME = {
  airbnb: ["airbnb_codes", "airbnbCode"],
  booking: ["booking_refs", "bookingRef"],
};

function vientDunImportCsv(r) {
  if (r.origine_import === "csv_airbnb" || r.origine_import === "csv_booking")
    return true;
  return typeof r.notes === "string" && /^Import CSV/.test(r.notes);
}

function codesSupprimes(data, lid, plateforme) {
  const [seau, champ] = SEAU_SUPPRIME[plateforme];
  const arr =
    data &&
    data._tombstones &&
    data._tombstones[lid] &&
    data._tombstones[lid][seau];
  return new Set(
    Array.isArray(arr)
      ? arr
          .map((t) => t && t[champ])
          .filter(Boolean)
          .map(String)
      : [],
  );
}

// → [{ code, lid, mois, checkIn }] triés par mois, chambre, arrivée.
// Point B (relecture) : un code connu de Beds24 (V2, non annulé, arrivée à
// partir de la bascule de sa chambre, mois couvert par le relevé, même
// plateforme) ABSENT du relevé est signalé aussi (`source: "beds24"`) — ce
// sont les annulations que la V2 ne voit plus. `resasByLogement` facultatif ;
// `bascules` pour les tests.
export function codesAbsentsDuReleve(
  data,
  { rows, plan, lids, plateforme, resasByLogement, bascules } = {},
) {
  const champ = CHAMP_CODE[plateforme];
  if (!champ || !data || !plan || !Array.isArray(lids)) return [];
  const mois = new Set(
    [
      ...(plan.toCreate || []).map((c) => c && c.month),
      ...(plan.toUpdate || []).map((u) => u && u.month),
    ].filter(Boolean),
  );
  if (mois.size === 0) return [];
  const duReleve = new Set(
    (rows || [])
      .map((r) => r && r[champ])
      .filter(Boolean)
      .map(String),
  );
  const vus = new Set();
  const out = [];
  for (const mk of [...mois].sort()) {
    for (const lid of lids) {
      const supprimes = codesSupprimes(data, lid, plateforme);
      const resas = data[mk] && data[mk][lid] && data[mk][lid].reservations;
      if (!Array.isArray(resas)) continue;
      for (const r of resas) {
        if (!r || !r[champ] || !vientDunImportCsv(r)) continue;
        const code = String(r[champ]);
        if (
          duReleve.has(code) ||
          supprimes.has(code) ||
          vus.has(lid + "|" + code)
        )
          continue;
        vus.add(lid + "|" + code);
        out.push({ code, lid, mois: mk, checkIn: r.checkIn || r.date || null });
      }
    }
  }
  if (resasByLogement && typeof resasByLogement.get === "function") {
    for (const lid of lids) {
      const bascule = dateBasculeCsv(lid, bascules);
      if (!bascule) continue;
      const rowsV2 = resasByLogement.get(lid);
      for (const row of Array.isArray(rowsV2) ? rowsV2 : []) {
        const code = row && row.raw_payload && row.raw_payload.apiReference;
        if (!code || String(row.source || "").toLowerCase() !== plateforme)
          continue;
        if (String(row.status || "").toLowerCase() === "cancelled") continue;
        const ci =
          typeof row.start_date === "string"
            ? row.start_date.slice(0, 10)
            : null;
        if (!ci || ci < bascule) continue;
        const mk =
          computeInvoiceMonthByNights(row.start_date, row.end_date) ||
          ci.slice(0, 7);
        if (!mois.has(mk)) continue;
        const c = String(code);
        if (duReleve.has(c) || codesSupprimes(data, lid, plateforme).has(c))
          continue;
        if (out.some((x) => x.code === c)) continue;
        out.push({ code: c, lid, mois: mk, checkIn: ci, source: "beds24" });
      }
    }
  }
  return out.sort(
    (a, b) =>
      a.mois.localeCompare(b.mois) ||
      a.lid.localeCompare(b.lid) ||
      String(a.checkIn || "").localeCompare(String(b.checkIn || "")),
  );
}

// Texte VALIDÉ PAR DIMITRI. null si rien à signaler.
export function texteCodesAbsents(absents, nomsParId) {
  if (!Array.isArray(absents) || absents.length === 0) return null;
  const parMois = new Map();
  for (const a of absents) {
    if (!parMois.has(a.mois)) parMois.set(a.mois, []);
    parMois.get(a.mois).push(a);
  }
  const morceaux = [...parMois].map(([mk, liste]) => {
    const items = liste.map((a) => {
      const jour = a.checkIn
        ? `${a.checkIn.slice(8, 10)}/${a.checkIn.slice(5, 7)}`
        : "—";
      const nom = (nomsParId && nomsParId[a.lid]) || a.lid;
      const beds24 = a.source === "beds24" ? ", vue dans Beds24" : "";
      return `${a.code} (arrivée ${jour}, ${nom}${beds24})`;
    });
    return `${formatMois(mk)} : ${items.join(", ")}`;
  });
  return `Déjà importées mais absentes de ce relevé — ${morceaux.join(" ; ")}. Rien n'est supprimé : vérifie s'il s'agit d'une annulation.`;
}

// ── D2 (décision Dimitri 03/10) : ÉCART RELEVÉ ↔ BEDS24 ──────────────────────
// À partir de la bascule, la ligne CSV fait foi. Si la V2 connaît le même
// code (non annulée) avec un autre net, on le SIGNALE dans la fenêtre
// d'import. Rien n'est compté deux fois (la projection retire la V2).
// → [{ code, lid, montantCsv, montantV2 }] ; montantV2 null = net Beds24
// illisible (dit tel quel, jamais remplacé par 0).
export function ecartsAvecV2(plan, resasByLogement, opts = {}) {
  if (
    !plan ||
    !resasByLogement ||
    typeof resasByLogement.forEach !== "function"
  )
    return [];
  const v2ParCode = new Map();
  resasByLogement.forEach((rows) => {
    for (const row of Array.isArray(rows) ? rows : []) {
      const code = row && row.raw_payload && row.raw_payload.apiReference;
      if (!code || String(row.status || "").toLowerCase() === "cancelled")
        continue;
      if (!v2ParCode.has(String(code))) v2ParCode.set(String(code), row);
    }
  });
  const lignes = [
    ...(plan.toCreate || []).map((c) => ({
      lid: c.logId,
      code: c.resa && (c.resa.airbnbCode || c.resa.bookingRef),
      checkIn: c.resa && c.resa.checkIn,
      montant: c.resa && c.resa.montant,
    })),
    ...(plan.toUpdate || []).map((u) => ({
      lid: u.logId,
      code: u.airbnbCode || u.bookingRef,
      checkIn: u.new && u.new.checkIn,
      montant: u.new && u.new.montant,
    })),
  ];
  const out = [];
  for (const l of lignes) {
    if (!l.code || !v2ParCode.has(String(l.code))) continue;
    const bascule = dateBasculeCsv(l.lid, opts.bascules);
    if (
      !bascule ||
      typeof l.checkIn !== "string" ||
      l.checkIn.slice(0, 10) < bascule
    )
      continue;
    let montantV2 = null;
    try {
      montantV2 = Number(
        transformV2Resa(v2ParCode.get(String(l.code))).montant,
      );
    } catch (e) {
      console.error("[ecartsAvecV2] net Beds24 illisible", {
        code: l.code,
        motif: e && e.message,
      });
    }
    const montantCsv = Number(l.montant) || 0;
    // Relecture H : une correction de net posée par code dans CETTE chambre
    // s'applique à la ligne du relevé (c'est elle qui compte) → toujours dite.
    const corrLid = opts.corrections && opts.corrections[l.lid];
    const correction =
      corrLid &&
      Object.prototype.hasOwnProperty.call(corrLid, String(l.code)) &&
      Number.isFinite(Number(corrLid[String(l.code)]))
        ? Math.round(Number(corrLid[String(l.code)]) * 100) / 100
        : null;
    if (
      correction == null &&
      montantV2 != null &&
      Number.isFinite(montantV2) &&
      Math.abs(montantV2 - montantCsv) < 0.005
    )
      continue;
    out.push({
      ...(correction != null ? { correction } : {}),
      code: String(l.code),
      lid: l.lid,
      montantCsv,
      montantV2: Number.isFinite(montantV2) ? montantV2 : null,
    });
  }
  return out;
}

const euros = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;

// Texte VALIDÉ PAR DIMITRI. null si rien à signaler.
export function texteEcartsV2(ecarts, nomsParId) {
  if (!Array.isArray(ecarts) || ecarts.length === 0) return null;
  const items = ecarts.map((e) => {
    const nom = (nomsParId && nomsParId[e.lid]) || e.lid;
    const v2 =
      e.montantV2 == null
        ? "montant Beds24 illisible"
        : `Beds24 ${euros(e.montantV2)}`;
    const corr =
      e.correction != null
        ? `, ta correction ${euros(e.correction)} s'applique`
        : "";
    return `${e.code} (${nom}) : relevé ${euros(e.montantCsv)}, ${v2}${corr}`;
  });
  // Relecture H : avec une correction, ce n'est plus « le relevé qui compte ».
  const fin = ecarts.some((e) => e.correction != null)
    ? "Compté une seule fois : ta correction quand il y en a une, sinon le relevé."
    : "C'est le relevé qui compte, une seule fois.";
  return `Montant différent de Beds24 — ${items.join(" ; ")}. ${fin}`;
}

// ── E (relecture) : « Restaurer » refusé si le code vit ailleurs ─────────────
// Une ligne supprimée d'une chambre puis réimportée dans la bonne (même
// groupe Booking) : la restaurer ferait porter le même séjour par deux
// chambres. → id de l'autre chambre où le code vit (présent, non supprimé),
// sinon null.
export function chambreOuLeCodeVit(data, lid, code, groupe) {
  if (!data || !code || !Array.isArray(groupe)) return null;
  const c = String(code);
  const mks = Object.keys(data).filter((mk) => /^\d{4}-\d{2}$/.test(mk));
  for (const autre of groupe) {
    if (!autre || autre === lid) continue;
    const t = data._tombstones && data._tombstones[autre];
    const supprime = (t && Array.isArray(t.booking_refs) ? t.booking_refs : [])
      .concat(t && Array.isArray(t.airbnb_codes) ? t.airbnb_codes : [])
      .some((x) => x && String(x.bookingRef || x.airbnbCode) === c);
    if (supprime) continue;
    const vit = mks.some((mk) => {
      const resas = data[mk] && data[mk][autre] && data[mk][autre].reservations;
      return (
        Array.isArray(resas) &&
        resas.some((r) => r && String(r.bookingRef || r.airbnbCode || "") === c)
      );
    });
    if (vit) return autre;
  }
  return null;
}

// Texte VALIDÉ PAR DIMITRI.
export const texteRestaurationRefusee = (nomChambre) =>
  `Restauration impossible : cette réservation est déjà comptée dans ${nomChambre}. Supprime-la là-bas d'abord.`;
